import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  Rectangle,
} from "recharts";
import { AppProvider } from "../../ContextApi/AppProvider";
import { PedidoService } from "../../Services/PedidoService";
import { FiltroRelatorio } from "../Shared/Interfaces/Pedido";
import { RelatorioPedidosPorDiaSemana } from "../Shared/Interfaces/RelatorioPedidosPorDiaSemana";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DefaultLoader from "../Shared/DefaultLoader";
import colors from "../../theme/colors";

export default function RelatorioPedidosPorDiaSemanaComponent() {
  const contextApi = useContext(AppProvider);
  const pedidoService = new PedidoService();
  const [loading, setLoading] = useState(false);
  const [dataInicial, setDataInicial] = useState<Dayjs>(
    dayjs().subtract(7, "day")
  );
  const [dataFinal, setDataFinal] = useState<Dayjs>(dayjs());
  const [relatorioPedidosPorDiaSemana, setRelatorioPedidosPorDiaSemana] = useState<
    RelatorioPedidosPorDiaSemana[]
  >([]);

  const PesquisarPor = () => {
    setLoading(true);
    const filtroPesquisa: FiltroRelatorio = {
      dataInicial: dataInicial != null ? dataInicial.toISOString() : null,
      dataFinal: dataFinal != null ? dataFinal.toISOString() : null,
    };

    pedidoService
      .gerarRelatorioPedidosPorDiaSemana(filtroPesquisa)
      .then((response) => {
        if (response.status === 200) {
          // Garantir que sempre seja um array
          const data = Array.isArray(response.data) ? response.data : [];
          setRelatorioPedidosPorDiaSemana(data);
        } else if (response.status === 204) {
          // Limpar os cards e mostrar mensagem
          setRelatorioPedidosPorDiaSemana([]);
          contextApi.showMessage("Nenhum dado encontrado para o período selecionado", true, "info");
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        // Verificar se é status 204
        if (error.response?.status === 204) {
          setRelatorioPedidosPorDiaSemana([]);
          contextApi.showMessage("Nenhum dado encontrado para o período selecionado", true, "info");
        } else {
          // Extrair mensagem de erro do backend
          let errorMessage = "Erro ao gerar relatório";
          if (error.response?.data) {
            if (typeof error.response.data === "string") {
              errorMessage = error.response.data;
            } else if (error.response.data?.message) {
              errorMessage = error.response.data.message;
            } else if (error.response.data?.error) {
              errorMessage = error.response.data.error;
            }
          }
          contextApi.showMessage(errorMessage, true, "error");
          // Em caso de erro, garantir que seja um array vazio
          setRelatorioPedidosPorDiaSemana([]);
        }
      });
  };

  // Função para ordenar os dados por dayOfWeek
  const ordenarDadosPorDiaSemana = (dados: RelatorioPedidosPorDiaSemana[]) => {
    return [...dados].sort((a, b) => a.dayOfWeek - b.dayOfWeek);
  };

  // Função para formatar data no formato DD/MM
  const formatarData = (data: string | undefined): string => {
    if (!data) return "";
    try {
      const date = dayjs(data);
      if (date.isValid()) {
        return date.format("DD/MM");
      }
    } catch (error) {
      // Se não conseguir formatar, retorna vazio
    }
    return "";
  };

  // Função para encontrar a data correspondente ao dayOfWeek no período
  // Retorna a primeira ocorrência do dia da semana no período
  const encontrarDataPorDayOfWeek = (dayOfWeek: number): string => {
    // dayjs usa: 0 = domingo, 1 = segunda, 2 = terça, 3 = quarta, 4 = quinta, 5 = sexta, 6 = sábado
    // Se a API retorna dayOfWeek 3 como "quarta-feira", então está usando o mesmo padrão do dayjs
    
    let dataAtual = dataInicial.clone();
    const dataFim = dataFinal;
    
    // Buscar a primeira ocorrência do dia da semana no período
    while (dataAtual.isBefore(dataFim) || dataAtual.isSame(dataFim, 'day')) {
      const diaSemanaAtual = dataAtual.day(); // 0 = domingo, 3 = quarta
      
      if (diaSemanaAtual === dayOfWeek) {
        return dataAtual.format("DD/MM");
      }
      
      dataAtual = dataAtual.add(1, 'day');
    }
    
    return "";
  };

  // Processar dados para o gráfico
  const processarDadosParaGrafico = () => {
    if (!Array.isArray(relatorioPedidosPorDiaSemana) || !relatorioPedidosPorDiaSemana.length) {
      return [];
    }

    const dadosOrdenados = ordenarDadosPorDiaSemana(relatorioPedidosPorDiaSemana);
    
    return dadosOrdenados.map(item => {
      // Primeiro tenta pegar da API, se não tiver, calcula baseado no período
      let dataFormatada = formatarData(item.data || item.dataCriacao);
      
      if (!dataFormatada) {
        // Calcula a data baseado no dayOfWeek e período filtrado
        dataFormatada = encontrarDataPorDayOfWeek(item.dayOfWeek);
      }
      
      const labelCompleto = dataFormatada 
        ? `${dataFormatada} ${item.diaSemana}` 
        : item.diaSemana;
      
      return {
        diaSemana: item.diaSemana,
        data: item.data,
        labelCompleto: labelCompleto,
        dataFormatada: dataFormatada,
        labelEixoX: `${dataFormatada} - ${item.diaSemana}`,
        itemOriginal: item, // Guardar item original para usar no tooltip
        "Total Pneus": item.totalPneus,
        "Total Outros": item.totalOutros,
      };
    });
  };

  // Componente de tooltip personalizado
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // No Recharts, o payload completo está em payload[0].payload
      // O label vem do dataKey do XAxis (que é "diaSemana")
      const dataItem = payload[0]?.payload;
      
      // Tentar pegar o labelCompleto do payload, se não tiver, construir a partir do label
      let labelCompleto = dataItem?.labelCompleto;
      
      // Se não tiver labelCompleto, tentar construir a partir da dataFormatada
      if (!labelCompleto && dataItem?.dataFormatada) {
        labelCompleto = `${dataItem.dataFormatada} ${label}`;
      } else if (!labelCompleto) {
        // Se ainda não tiver, usar apenas o label (diaSemana)
        labelCompleto = label;
      }
      
      return (
        <Paper sx={{ p: 2, boxShadow: 3, bgcolor: 'white' }}>
          <Typography variant="h6" sx={{ color: colors.bluePrimary, mb: 1 }}>
            📅 {labelCompleto}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: entry.color }}>
                {entry.name}: {entry.value} {entry.name === "Total Pneus" ? "pneus" : "outros"}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  const dadosGrafico = processarDadosParaGrafico();
  
  // Garantir que sempre seja um array antes de usar reduce
  const dadosArray = Array.isArray(relatorioPedidosPorDiaSemana) ? relatorioPedidosPorDiaSemana : [];
  const totalPneus = dadosArray.reduce((sum, item) => sum + (item.totalPneus || 0), 0);
  const totalOutros = dadosArray.reduce((sum, item) => sum + (item.totalOutros || 0), 0);
  
  React.useEffect(() => {
    PesquisarPor();
  }, []);

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 1, sm: 2 } }}>
        <Grid container spacing={2}>
          {/* Linha 1 - Filtros de Data */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>🗓️ Filtros de Período</h4>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <DemoContainer components={["DatePicker"]}>
                      <DatePicker
                        label="Data Inicial"
                        defaultValue={dataInicial}
                        value={dataInicial}
                        format="DD/MM/YYYY"
                        onChange={(newValue) =>
                          setDataInicial(newValue ?? dataInicial)
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                          },
                        }}
                      />
                    </DemoContainer>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DemoContainer components={["DatePicker"]}>
                      <DatePicker
                        value={dataFinal}
                        onChange={(newValue) =>
                          setDataFinal(newValue ?? dataFinal)
                        }
                        label="Data Final"
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                          },
                        }}
                      />
                    </DemoContainer>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.5}>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{ height: '40px', width: "100%" }}
                    onClick={() => PesquisarPor()}
                  >
                    {loading ? (
                      <DefaultLoader
                        height="2.5em"
                        width="2.5em"
                        colors={[
                          "#1876D2",
                          "#1876D2",
                          "#1876D2",
                          "#1876D2",
                          "#1876D2",
                        ]}
                        textInfo="Gerando Relatório..."
                      />
                    ) : (
                      "Pesquisar"
                    )}
                  </Button>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </Paper>
          </Grid>

          {/* Linha 2 - Cards de Resumo */}
          {Array.isArray(relatorioPedidosPorDiaSemana) && relatorioPedidosPorDiaSemana.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 1, mb: 1 }}>
                <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📊 Resumo Geral</h4>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        bgcolor: "#1976D2", 
                        borderRadius: 2, 
                        color: "white",
                        boxShadow: 2,
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {totalPneus}
                      </Typography>
                      <Typography variant="body2">Total de Pneus</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        bgcolor: "#2E7D32", 
                        borderRadius: 2, 
                        color: "white",
                        boxShadow: 2,
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {totalOutros}
                      </Typography>
                      <Typography variant="body2">Total de Outros</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        bgcolor: "success.main", 
                        borderRadius: 2, 
                        color: "white",
                        boxShadow: 2,
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {totalPneus + totalOutros}
                      </Typography>
                      <Typography variant="body2">Total Produtos</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Linha 3 - Gráfico */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📊 Análise por Dia da Semana</h4>
              
              <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                {dadosGrafico.length > 0 ? (
                  <Box sx={{ height: '500px', width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={dadosGrafico}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        barCategoryGap="20%"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="labelEixoX" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          fontSize={12}
                        />
                        <YAxis 
                          label={{ 
                            value: 'Quantidade de Itens', 
                            angle: -90, 
                            position: 'insideLeft',
                            fontSize: 12
                          }}
                        />
                        <Tooltip content={CustomTooltip} />
                        <Legend 
                          verticalAlign="top" 
                          height={36}
                          wrapperStyle={{ paddingBottom: '10px' }}
                        />
                        
                        <Bar
                          dataKey="Total Pneus"
                          name="Total Pneus"
                          fill="#1976D2"
                          activeBar={<Rectangle fill="#1976D2" stroke="#1C1C1C" />}
                        />
                        <Bar
                          dataKey="Total Outros"
                          name="Total Outros"
                          fill="#2E7D32"
                          activeBar={<Rectangle fill="#2E7D32" stroke="#1C1C1C" />}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    
                    {/* Resumo dos dados abaixo do gráfico */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
                        📊 Total de {dadosArray.length} dias da semana no período analisado
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Nenhum dado disponível para o período selecionado
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
